import { useState, useRef, useEffect } from 'react';
import { EXPIRATION_LABELS } from '@reqshare/shared';
import type { ExpirationOption } from '@reqshare/shared';
import './OwnerMenu.scss';

const EXPIRATION_OPTIONS: ExpirationOption[] = ['1h', '24h', '7d', '30d'];

interface OwnerMenuProps {
  isPro: boolean;
  onChangeExpiration: (expiration: ExpirationOption) => void;
  onDelete: () => void;
}

export function OwnerMenu({ isPro, onChangeExpiration, onDelete }: OwnerMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showExpiration, setShowExpiration] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
        setShowExpiration(false);
        setConfirmDelete(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleExpirationClick(exp: ExpirationOption) {
    onChangeExpiration(exp);
    setMenuOpen(false);
    setShowExpiration(false);
  }

  function handleDeleteClick() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    onDelete();
    setMenuOpen(false);
    setConfirmDelete(false);
  }

  return (
    <div className="owner-menu" ref={menuRef}>
      <button
        className="owner-menu__trigger"
        onClick={() => {
          setMenuOpen(!menuOpen);
          setShowExpiration(false);
          setConfirmDelete(false);
        }}
        aria-label="Request options"
      >
        &#8943;
      </button>
      {menuOpen && (
        <div className="owner-menu__dropdown">
          {!showExpiration ? (
            <>
              <button
                className="owner-menu__item"
                onClick={() => setShowExpiration(true)}
              >
                Change expiration
              </button>
              <button
                className="owner-menu__item owner-menu__item--danger"
                onClick={handleDeleteClick}
              >
                {confirmDelete ? 'Confirm delete' : 'Delete'}
              </button>
            </>
          ) : (
            <>
              <button
                className="owner-menu__back"
                onClick={() => setShowExpiration(false)}
              >
                &larr; Back
              </button>
              {EXPIRATION_OPTIONS.map((exp) => (
                <button
                  key={exp}
                  className="owner-menu__item"
                  onClick={() => handleExpirationClick(exp)}
                >
                  {EXPIRATION_LABELS[exp]}
                </button>
              ))}
              {isPro && (
                <button
                  className="owner-menu__item"
                  onClick={() => handleExpirationClick('never')}
                >
                  {EXPIRATION_LABELS['never']}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
